import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, EMPTY, take } from 'rxjs';
import { JobInvitationStatus, Uuid } from '../../../../../../graphql';
import { SingleClickModule } from '../../../../../core/directives/single-click.directive';
import { BxLogger } from '../../../../../core/services/bx-logger.service';
import { DialogService } from '../../../../../core/services/dialog.service';
import { HelperService } from '../../../../../core/services/helper.service';
import { JobCrewInvitationsApiService, JobInvitation, TenantUser } from './domain/job-crew-invitations.api.service';
import { InviteContactComponent } from './invite-contact/invite-contact.component';

@Component({
    selector: 'app-job-crew-invitations',
    imports: [CommonModule, SingleClickModule, MatTooltipModule],
    templateUrl: './job-crew-invitations.component.html',
    styleUrls: [
        '../../../../../shared/composable-styles/table.scss',
        '../../../../../shared/composable-styles/button.scss',
        '../../../../../shared/composable-styles/chip.scss',
        '../../../../../shared/composable-styles/text.scss',
        './job-crew-invitations.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCrewInvitationsComponent implements OnInit {
    #api = inject(JobCrewInvitationsApiService);
    #dialog = inject(DialogService);
    #logger = inject(BxLogger);
    #helper = inject(HelperService);

    jobId = input.required<Uuid>();
    inviteStatus = JobInvitationStatus;

    tenantUsers = signal<TenantUser[]>([]);
    invitations = signal<JobInvitation[]>([]);

    ngOnInit() {
        this.#api
            .getInvitations(this.jobId())
            .pipe(take(1))
            .subscribe((res) => {
                if (res.data.usersInCurrentTenant?.length) this.tenantUsers.set(res.data.usersInCurrentTenant);
                if (res.data.jobInvitations?.items?.length) this.invitations.set(res.data.jobInvitations.items);
            });
    }

    deleteJobInvitation(jobInvitation: JobInvitation) {
        const name =
            `${jobInvitation.inviteeFirstName} ${jobInvitation.inviteeLastName}` +
            (jobInvitation.contact?.businessName ? ` (${jobInvitation.contact?.businessName})` : '');
        const message =
            jobInvitation.status === JobInvitationStatus.Accepted
                ? 'Removing this crew member will remove their access to the job on Buildxact Onsite. '
                : 'Removing this crew invite will revoke their invitation to the job on Buildxact Onsite.';
        this.#dialog
            .show({
                title: `Are you sure you want to remove ${name}?`,
                text: message,
                okText: 'Remove',
            })
            .then(() => {
                this.#api
                    .deleteInvitation(jobInvitation.iD_JobInvitation)
                    .pipe(
                        take(1),
                        catchError(() => {
                            this.#logger.error('Something went wrong');
                            return EMPTY;
                        })
                    )
                    .subscribe((res) => {
                        if (!res.data?.deleteJobInvitation) return this.#logger.error('Something went wrong');

                        const message =
                            jobInvitation.status === JobInvitationStatus.Accepted ? 'Crew member removed' : 'Invitation revoked';
                        this.#logger.success(message);
                        this.invitations.set(this.invitations().filter((i) => i.iD_JobInvitation !== jobInvitation.iD_JobInvitation));
                    });
            })
            .catch(() => {});
    }

    inviteContact() {
        const modalRef = this.#helper.showModalAside(InviteContactComponent, { initialState: { jobId: this.jobId() } });

        modalRef?.content?.result.subscribe((res) => {
            // User can re-invite the same contact, don't add a duplicate record
            if (!this.invitations().find((inv) => inv.iD_JobInvitation === res.iD_JobInvitation)) {
                this.invitations.set([...this.invitations(), res]);
            }
        });
    }
}
